import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useReducer,
} from 'react'

import { useAppDispatch } from 'common/hooks/redux'
import {
  hideRightPanel,
  showRightPanel,
} from 'features/unifiedFlow/store/unifiedFlowSlice'

type State = {
  selectedVariantCombinationId: string | undefined
}

type VariantLanguageContextType = {
  state: State
  selectVariant: (value: { combinationId: string }) => void
  clearVariant: () => void
}

type VariantLanguageContextReducerActions =
  | { type: 'selectVariant'; combinationId: string }
  | { type: 'clearVariant' }

const initialState: State = {
  selectedVariantCombinationId: undefined,
}

const variantLanguageContextReducer = (
  state: State,
  action: VariantLanguageContextReducerActions
) => {
  switch (action.type) {
    case 'selectVariant': {
      return {
        ...state,
        selectedVariantCombinationId: action.combinationId,
      }
    }
    case 'clearVariant': {
      return initialState
    }
    default:
      return state
  }
}

const VariantLanguageContext = createContext<VariantLanguageContextType>({
  state: initialState,
  selectVariant: () => undefined,
  clearVariant: () => undefined,
})

const VariantLanguageContextProvider = ({
  children,
}: {
  children: ReactNode
}) => {
  const dispatch = useAppDispatch()
  const [state, dispatchVariantLanguageContextState] = useReducer(
    variantLanguageContextReducer,
    initialState
  )

  const selectVariant = useCallback(
    ({ combinationId }: { combinationId: string }) => {
      dispatchVariantLanguageContextState({
        type: 'selectVariant',
        combinationId,
      })
      dispatch(showRightPanel())
    },
    [dispatch]
  )

  const clearVariant = useCallback(() => {
    dispatchVariantLanguageContextState({ type: 'clearVariant' })
    dispatch(hideRightPanel())
  }, [dispatch])

  const value = { state, selectVariant, clearVariant }

  return (
    <VariantLanguageContext.Provider value={value}>
      {children}
    </VariantLanguageContext.Provider>
  )
}

const useVariantLanguageContext = () => {
  const context = useContext(VariantLanguageContext)
  if (!context) {
    throw new Error(
      'useVariantLanguageContext must be used within VariantLanguageContextProvider'
    )
  }
  return context
}

export { VariantLanguageContextProvider, useVariantLanguageContext }
